import { create } from 'zustand';
import type { Product, CartItem } from '@/types/index';
import { toast } from 'sonner';

interface CartState {
  products: CartItem[];
  availableCredits: number;
  addProduct: (product: Product, quantity?: number) => void;
  removeProduct: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  getTotalCartCredits: () => number;
  updateAvailableCredits: (credits: number) => void;
  resetCart: () => void;
}

// Get initial available credits from localStorage if available
const getInitialAvailableCredits = (): number => {
  try {
    const userDataString = localStorage.getItem('liga:userData');
    if (userDataString) {
      const userData = JSON.parse(userDataString);
      if (userData?.partnerDatum) {
        const {
          totalCredit = 0,
          extraCredit = 0,
          usedCredit = 0,
        } = userData.partnerDatum;
        return totalCredit + extraCredit - usedCredit;
      }
    }
  } catch (error) {
    console.error('Error parsing userData from localStorage', error);
  }
  return 0; // Default to 0 if no data is available
};

// Get initial cart products from localStorage if available
const getInitialCartProducts = (): CartItem[] => {
  try {
    const cartString = localStorage.getItem('liga:cart');
    if (cartString) {
      return JSON.parse(cartString);
    }
  } catch (error) {
    console.error('Error parsing cart from localStorage', error);
  }
  return []; // Default to empty array if no data is available
};

// Save cart products to localStorage
const saveCartToLocalStorage = (products: CartItem[]) => {
  try {
    localStorage.setItem('liga:cart', JSON.stringify(products));
  } catch (error) {
    console.error('Error saving cart to localStorage', error);
  }
};

// Clear cart data from localStorage
const clearCartFromLocalStorage = () => {
  try {
    localStorage.removeItem('liga:cart');
  } catch (error) {
    console.error('Error clearing cart from localStorage', error);
  }
};

export const useCartStore = create<CartState>((set, get) => ({
  products: getInitialCartProducts(),
  availableCredits: getInitialAvailableCredits(),

  updateAvailableCredits: (credits: number) => {
    set({ availableCredits: credits });
  },

  resetCart: () => {
    clearCartFromLocalStorage();
    set({ products: [] });
  },

  addProduct: (product, quantity = 1) => {
    const currentProducts = get().products;
    const totalCartCredits = get().getTotalCartCredits();
    const availableCredits = get().availableCredits;

    // Check if product already exists
    const existingProduct = currentProducts.find((p) => p.id === product.id);
    if (existingProduct) {
      const newQuantity = existingProduct.quantity + quantity;
      const additionalCost = product.price * quantity;

      // Check if enough credits are available for additional quantity
      if (totalCartCredits + additionalCost > availableCredits) {
        toast.error('Nincs elég kredit a termék hozzáadásához.');
        return;
      }

      // Update quantity of existing product
      const updatedProducts = currentProducts.map((p) =>
        p.id === product.id ? { ...p, quantity: newQuantity } : p
      );

      saveCartToLocalStorage(updatedProducts);
      set({ products: updatedProducts });
      toast.success(`${product.name} mennyiség frissítve.`);
      return;
    }

    // Check if enough credits are available for new product
    if (totalCartCredits + product.price * quantity > availableCredits) {
      toast.error('Nincs elég kredit a termék hozzáadásához.');
      return;
    }

    // Add new product to cart
    const updatedProducts = [...currentProducts, { ...product, quantity }];
    saveCartToLocalStorage(updatedProducts);
    set({ products: updatedProducts });
    toast.success(`${product.name} hozzáadva a kosárhoz.`);
  },

  updateQuantity: (productId: string, quantity: number) => {
    const currentProducts = get().products;
    const product = currentProducts.find((p) => p.id === productId);

    if (!product) return;

    const currentTotal = get().getTotalCartCredits();
    const currentProductTotal = product.price * product.quantity;
    const newProductTotal = product.price * quantity;
    const newTotal = currentTotal - currentProductTotal + newProductTotal;

    if (newTotal > get().availableCredits) {
      toast.error('Nincs elég kredit a mennyiség módosításához.');
      return;
    }

    if (quantity <= 0) {
      get().removeProduct(productId);
      return;
    }

    const updatedProducts = currentProducts.map((p) =>
      p.id === productId ? { ...p, quantity } : p
    );

    saveCartToLocalStorage(updatedProducts);
    set({ products: updatedProducts });
  },

  removeProduct: (productId) => {
    const updatedProducts = get().products.filter((p) => p.id !== productId);
    saveCartToLocalStorage(updatedProducts);
    set({ products: updatedProducts });
    toast.success('Termék eltávolítva a kosárból.');
  },

  getTotalCartCredits: () => {
    return get().products.reduce(
      (total, product) => total + product.price * product.quantity,
      0
    );
  },
}));
